import { captureException } from '@sentry/react';
import { action, computed, makeObservable, observable, override, runInAction } from 'mobx';
import LocalizedStrings from 'strings';
import { ServiceContainer } from '../../providers';
import { LocalizationService } from '../../services';
import { SchoolDataStore } from '../../stores';
import {
  AppBaseStaticDialogViewModel,
  CancelDialogActionButtonConfiguration,
  DialogActionButtonConfiguration,
  SaveDialogActionButtonConfiguration,
  StaticDialogViewModel
} from '../utils';

const emailRegex =
  /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/;

export interface AdminSchoolInviteTeachersDialogViewModel extends StaticDialogViewModel {
  readonly emails: string[];
  isEmailValid(email: string): boolean;
  addEmail(email: string): void;
  removeEmail(email: string): void;
}

export class AppAdminSchoolInviteTeachersDialogViewModel
  extends AppBaseStaticDialogViewModel
  implements AdminSchoolInviteTeachersDialogViewModel
{
  private _emails = observable.set<string>();
  @observable private _isImporting = false;

  private readonly _saveButtonConfig: SaveDialogActionButtonConfiguration;
  private readonly _cancelButtonConfig: CancelDialogActionButtonConfiguration;

  constructor(
    private readonly _schoolId: string,
    onDismiss: () => Promise<void>,
    private readonly _localization: LocalizationService = ServiceContainer.services.localization,
    private readonly _schoolStore: SchoolDataStore = ServiceContainer.services.schoolStore
  ) {
    super(onDismiss);
    makeObservable(this);

    this._cancelButtonConfig = new CancelDialogActionButtonConfiguration('main', this._localization, () =>
      this.dismiss()
    );

    this._saveButtonConfig = new SaveDialogActionButtonConfiguration(
      'main',
      this._localization,
      () => this.sendInvite(),
      () => LocalizedStrings.admin.teachersList.invite.submitButton(),
      'send'
    );
  }

  @override
  get actions(): DialogActionButtonConfiguration[] {
    this._saveButtonConfig.showLoading = this._isImporting;
    this._saveButtonConfig.isEnabled = !this._isImporting && this._emails.size > 0;
    return [this._cancelButtonConfig, this._saveButtonConfig];
  }

  @computed
  get emails(): string[] {
    // Displaying the last entered email at the top of the list.
    return Array.from(this._emails.values()).reverse();
  }

  isEmailValid(email: string): boolean {
    const actualEmails = this.splitEmails(email);
    return actualEmails.every((e) => emailRegex.test(e));
  }

  @action
  addEmail(email: string) {
    const actualEmails = this.splitEmails(email);
    actualEmails.forEach((e) => this._emails.add(e));
  }

  @action
  removeEmail(email: string) {
    this._emails.delete(email);
  }

  private async sendInvite() {
    runInAction(() => {
      this._isImporting = true;
      this._error = undefined;
    });

    try {
      await this._schoolStore.inviteTeachersToSchool(this._schoolId, this.emails);
      await this.dismiss();
    } catch (e) {
      captureException(e);
      runInAction(() => (this._error = LocalizedStrings.admin.teachersList.invite.unexpectedErrorMessage()));
    } finally {
      runInAction(() => (this._isImporting = false));
    }
  }

  private splitEmails(emails: string): string[] {
    // We support entries like "John Doe <jdoe@example.com>, Jack Black <jack@iscool.net>" which is common
    // when copying from email clients. We also want to support a space separator when entering basic emails.
    // Since "<" is not a valid email char in the first place, we assume its presence means we must not split
    // on spaces.
    const separators = emails.includes('<') ? /[,;]+/ : /[\s,;]+/;
    return emails
      .split(separators)
      .map((e) => e.trim())
      .filter((e) => e.length > 0)
      .map((e) => {
        const start = e.indexOf('<');

        if (start >= 0) {
          const end = e.indexOf('>', start);

          if (end >= 0) {
            return e.substring(start + 1, end);
          }

          // We do not perform validation, this is done elsewhere.
        }

        return e;
      });
  }
}
